import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ACTIVE_JOB_STATUSES, cancelJob, JOB_REGISTER_EVENT, listJobs, loadJobSnapshot, retryJob, TERMINAL_JOB_STATUSES } from '../../services/jobs';

const JobContext = createContext(null);
const STORAGE_KEY = 'testsuite_active_jobs_v1';

const readStoredJobs = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); } catch (_err) { return []; }
};

export const JobProvider = ({ enabled = true, children, onJobCompleted }) => {
  const [jobsById, setJobsById] = useState({});
  const [itemsById, setItemsById] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const timers = useRef(new Map());
  const attempts = useRef(new Map());
  const completed = useRef(new Set());

  const upsert = useCallback((job, items) => {
    if (!job?.id) return;
    setJobsById((current) => ({ ...current, [job.id]: job }));
    if (items) setItemsById((current) => ({ ...current, [job.id]: items }));
  }, []);

  const registerJob = useCallback((job) => upsert(job), [upsert]);

  useEffect(() => {
    const register = (event) => registerJob(event.detail);
    window.addEventListener(JOB_REGISTER_EVENT, register);
    return () => window.removeEventListener(JOB_REGISTER_EVENT, register);
  }, [registerJob]);

  const refreshJob = useCallback(async (reference) => {
    const snapshot = await loadJobSnapshot(reference);
    upsert(snapshot.job, snapshot.items);
    return snapshot;
  }, [upsert]);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    setLoading(true);
    Promise.allSettled([
      listJobs({ limit: 100 }),
      ...readStoredJobs().map((reference) => loadJobSnapshot(reference)),
    ]).then((results) => {
      if (cancelled) return;
      const generic = results[0].status === 'fulfilled' ? results[0].value : [];
      generic.forEach((job) => upsert(job));
      results.slice(1).forEach((result) => {
        if (result.status === 'fulfilled') upsert(result.value.job, result.value.items);
      });
      if (results.every((result) => result.status === 'rejected')) setError('Background jobs could not be restored.');
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [enabled, upsert]);

  useEffect(() => {
    const active = Object.values(jobsById).filter((job) => ACTIVE_JOB_STATUSES.has(job.status));
    localStorage.setItem(STORAGE_KEY, JSON.stringify(active.map(({ id, adapter, application_id }) => ({ id, adapter, application_id }))));

    active.forEach((job) => {
      if (timers.current.has(job.id)) return;
      const poll = async () => {
        try {
          const snapshot = await refreshJob(job);
          attempts.current.set(job.id, 0);
          if (ACTIVE_JOB_STATUSES.has(snapshot.job.status)) {
            const base = snapshot.job.status === 'queued' ? 4500 : 2500;
            timers.current.set(job.id, setTimeout(poll, Math.round(base * (0.85 + Math.random() * 0.3))));
          } else {
            timers.current.delete(job.id);
          }
        } catch (_err) {
          const attempt = (attempts.current.get(job.id) || 0) + 1;
          attempts.current.set(job.id, attempt);
          timers.current.set(job.id, setTimeout(poll, Math.min(30000, 2000 * (2 ** attempt))));
        }
      };
      timers.current.set(job.id, setTimeout(poll, 250));
    });

    Object.values(jobsById).filter((job) => TERMINAL_JOB_STATUSES.has(job.status)).forEach((job) => {
      if (!completed.current.has(job.id)) {
        completed.current.add(job.id);
        onJobCompleted?.(job);
        window.dispatchEvent(new CustomEvent('testsuite:job-completed', { detail: job }));
      }
    });
  }, [jobsById, onJobCompleted, refreshJob]);

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  useEffect(() => {
    const restoreOtherTab = (event) => {
      if (event.key !== STORAGE_KEY || !event.newValue) return;
      readStoredJobs().forEach((reference) => refreshJob(reference).catch(() => {}));
    };
    window.addEventListener('storage', restoreOtherTab);
    return () => window.removeEventListener('storage', restoreOtherTab);
  }, [refreshJob]);

  const runAction = useCallback(async (job, action) => {
    upsert({ ...job, actionPending: action });
    try {
      const updated = action === 'cancel' ? await cancelJob(job) : await retryJob(job);
      upsert({ ...updated, actionPending: '' });
    } catch (actionError) {
      upsert({ ...job, actionPending: '', actionError: actionError.message });
    }
  }, [upsert]);

  const value = useMemo(() => ({
    jobs: Object.values(jobsById).sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)),
    itemsById, loading, error, registerJob, refreshJob,
    cancel: (job) => runAction(job, 'cancel'),
    retry: (job) => runAction(job, 'retry'),
  }), [error, itemsById, jobsById, loading, refreshJob, registerJob, runAction]);

  return <JobContext.Provider value={value}>{children}</JobContext.Provider>;
};

export const useJobs = () => {
  const value = useContext(JobContext);
  if (!value) throw new Error('useJobs must be used inside JobProvider.');
  return value;
};
